/**
 * Service Print Dialog - Diálogo de impressão para Reparações/Serviços
 * P0-PRINT-01: Migrado para Template Builder via renderDocumentPdf
 * 
 * Opções:
 * - Guia de Recepção (ao receber equipamento)
 * - Guia de Entrega Final (ao devolver equipamento reparado)
 */

import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  Download, Upload, FileText, CheckCircle, Copy, Printer, Info, Loader2
} from 'lucide-react';
import { printAPI } from '@/lib/api';
import { formatCurrency, formatDisplayOnlyCode } from '@/lib/utils';

export default function ServicePrintDialog({ 
  open, 
  onOpenChange, 
  service,
  onClose,
}) {
  const [printing, setPrinting] = useState(null);
  const [printerTarget, setPrinterTarget] = useState('a4');

  const documentCards = useMemo(() => ([
    {
      key: 'reception',
      docType: 'reception_guide',
      title: 'Guia de Recepção',
      description: 'Documento de entrada do equipamento',
      icon: Download,
      accent: 'text-amber-600',
      enabled: true,
      printerProfile: 'a4',
      testId: 'service-print-reception',
    },
    {
      key: 'quotation',
      docType: 'quotation',
      title: 'Cotação do Serviço',
      description: 'Orçamento técnico/comercial do processo',
      icon: FileText,
      accent: 'text-indigo-600',
      enabled: Boolean(service?.quotation),
      printerProfile: 'a4',
      testId: 'service-print-quotation',
    },
    {
      key: 'payment',
      docType: 'payment_receipt',
      title: 'Recibo / Comprovativo',
      description: 'Pagamentos parciais ou liquidação final',
      icon: CheckCircle,
      accent: 'text-emerald-600',
      enabled: Boolean((service?.payments || []).length),
      printerProfile: 'a4',
      testId: 'service-print-payment',
    },
    {
      key: 'delivery',
      docType: 'service_guide',
      title: 'Guia de Entrega Final',
      description: 'Documento de devolução do equipamento reparado',
      icon: Upload,
      accent: 'text-green-600',
      enabled: ['completed', 'delivered', 'ready_for_pickup'].includes(service?.status?.toLowerCase()),
      printerProfile: 'a4',
      testId: 'service-print-delivery',
    },
  ]), [service]);

  if (!service) return null;

  const handlePrint = async (type, docType, copyType = 'Original') => {
    if (printerTarget !== 'a4') {
      toast.error('Os documentos de Serviços/Reparações são emitidos em A4');
      return;
    }

    setPrinting(`${type}-${copyType}`);
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type: docType,
        service_id: service.id,
        location_id: service.location_id,
        module_group: docType === 'quotation' ? 'DOCUMENTOS' : 'SERVIÇOS',
        copy_type: copyType,
      });

      if (result.success) {
        if (result.usedFallback) {
          toast.warning('Template não configurado - usando modelo antigo', {
            description: result.warningMessage || 'Configure um template no Template Builder.',
            duration: 6000
          });
        } else {
          toast.success(`Documento aberto para impressão`);
        }
      } else {
        toast.error('Erro ao abrir documento', { description: result.error });
      }
    } catch (err) {
      toast.error('Erro inesperado ao imprimir');
    } finally {
      setPrinting(null);
    }
  };
  
  const handleClose = () => {
    onOpenChange(false);
    if (onClose) onClose();
  };

  const isLoading = (key) => printing === key;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="service-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5 text-blue-500" />
            Impressão - Serviço/Reparação
          </DialogTitle>
          <DialogDescription>{formatDisplayOnlyCode(service.service_number, service?.service_type === 'warranty_claim' ? 'WAR' : 'SRV')}</DialogDescription>
        </DialogHeader>
        
        {/* Service Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Equipamento</span>
            <span className="font-medium">{service.device_brand} {service.device_model}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Cliente</span>
            <span>{service.customer_name || 'Anónimo'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Estado</span>
            <Badge variant="outline">{service.status}</Badge>
          </div>
          {service.quotation?.total && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Valor Orçamento</span>
              <span className="font-bold text-green-600">
                {formatCurrency(service.quotation.total)}
              </span>
            </div>
          )}
        </div>
        
        <Separator />

        <div className="space-y-3" data-testid="service-printer-selection-panel">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-medium text-sm">Escolha de impressora</h3>
              <p className="text-xs text-muted-foreground">Os documentos de Serviços são A4; a térmica fica visível para validação do contexto da loja.</p>
            </div>
            <Badge variant="outline" data-testid="service-printer-selection-badge">
              Destino actual: {printerTarget === 'a4' ? 'A4' : 'Térmica'}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={printerTarget === 'a4' ? 'default' : 'outline'}
              onClick={() => setPrinterTarget('a4')}
              data-testid="service-printer-a4-button"
            >
              Impressora A4
            </Button>
            <Button
              type="button"
              variant={printerTarget === 'thermal' ? 'default' : 'outline'}
              onClick={() => setPrinterTarget('thermal')}
              data-testid="service-printer-thermal-button"
            >
              Impressora Térmica
            </Button>
          </div>
        </div>

        {printerTarget === 'thermal' && (
          <Alert className="bg-amber-50 border-amber-200" data-testid="service-printer-thermal-warning">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-xs text-amber-700">
              Neste fluxo, Guia de Recepção, Cotação, Recibos e Guia Final são emitidos em <strong>A4</strong>. Mude o destino para A4 para imprimir.
            </AlertDescription>
          </Alert>
        )}

        <Separator />
        
        {/* Print Options */}
        <div className="space-y-4">
          <h3 className="font-medium text-sm">Documentos Disponíveis</h3>

          {documentCards.map((card) => {
            const CardIcon = card.icon;
            const disabledForPrinter = printerTarget !== card.printerProfile;
            return (
              <div
                key={card.key}
                className={`border rounded-lg p-3 space-y-2 ${!card.enabled ? 'opacity-50' : ''}`}
                data-testid={`${card.testId}-card`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <CardIcon className={`h-5 w-5 ${card.accent}`} />
                    <div>
                      <p className="font-medium text-sm">{card.title} A4</p>
                      <p className="text-xs text-muted-foreground">{card.description}</p>
                    </div>
                  </div>
                  <Badge variant="outline" data-testid={`${card.testId}-printer-badge`}>
                    Impressora A4
                  </Badge>
                </div>

                <div className="flex gap-2">
                  {['Original', 'Duplicado'].map((copyType) => (
                    <Button
                      key={copyType}
                      size="sm"
                      variant="outline"
                      onClick={() => handlePrint(card.key, card.docType, copyType)}
                      disabled={printing !== null || !card.enabled || disabledForPrinter}
                      className="flex-1"
                      data-testid={`${card.testId}-${copyType === 'Original' ? 'original' : 'duplicate'}-btn`}
                    >
                      {isLoading(`${card.key}-${copyType}`) ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : copyType === 'Original' ? (
                        <FileText className="h-4 w-4 mr-1" />
                      ) : (
                        <Copy className="h-4 w-4 mr-1" />
                      )}
                      {copyType}
                    </Button>
                  ))}
                </div>

                {!card.enabled && (
                  <p className="text-xs text-muted-foreground" data-testid={`${card.testId}-disabled-note`}>
                    Documento ainda não disponível para o estado actual do serviço.
                  </p>
                )}
                {disabledForPrinter && card.enabled && (
                  <p className="text-xs text-amber-700" data-testid={`${card.testId}-printer-note`}>
                    Este documento exige contexto A4.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <Separator />

        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-xs text-blue-700">
            Os documentos utilizam os templates do <strong>Template Builder</strong>.
            Se aparecer aviso "Template não configurado", configure um template no Template Builder.
          </AlertDescription>
        </Alert>
        
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
